import { LoginFormPage, PageLoading } from '@ant-design/pro-components';
import { Alert, App } from 'antd';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FeishuQrLoginForm from '@/apps/auth/components/FeishuQrLoginForm';
import PhoneOtpLoginForm from '@/apps/auth/components/PhoneOtpLoginForm';
import { signInWithFeishuJssdkCode } from '@/apps/auth/services/feishu';
import {
  isFeishuClient,
  loadFeishuH5Sdk,
  requestFeishuAccessCode,
  waitFeishuH5SdkReady,
} from '@/apps/auth/utils/feishu-jssdk';
import {
  LOGIN_CONTEXT,
  LOGIN_CONTEXT_ERROR_MESSAGE,
  getLoginCallbackUrl,
  isFeishuSsoLoginType,
  resolveLoginErrorMessage,
  resolveLoginPostPath,
} from '@/apps/auth/utils/login';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import { authClient } from '@/common/utils/auth-client';
import { useQueryParams } from '@/common/hooks/use-query-params';

const BACKGROUND_IMAGE_URL =
  'https://mdn.alipayobjects.com/huamei_gcee1x/afts/img/A*y0ZTS6WLwvgAAAAAAAAAAAAADml6AQ/fmt.webp';
const BACKGROUND_VIDEO_URL =
  'https://gw.alipayobjects.com/v/huamei_gcee1x/afts/video/jXRBRK_VAwoAAAAAAAAAAAAAK4eUAQBr';
const HIDDEN_LOGIN_SUBMITTER = { render: () => null };

const LoginMessage = ({ content }) => <Alert title={content} type="error" showIcon />;

LoginMessage.propTypes = {
  content: PropTypes.node,
};

const Login = ({ loginContext = LOGIN_CONTEXT.TENANT }) => {
  const [userLoginState, setUserLoginState] = useState({});
  const [redirecting, setRedirecting] = useState(false);
  const [feishuLoginMode, setFeishuLoginMode] = useState('checking');
  const hasHandledAuthenticatedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [query] = useQueryParams();
  const redirect = Array.isArray(query.redirect) ? query.redirect.at(-1) : query.redirect;
  const { session } = useAppContext();
  const { user } = session;
  const { message } = App.useApp();
  const adminLogin = loginContext === LOGIN_CONTEXT.ADMIN;
  const ssoLogin = !adminLogin && isFeishuSsoLoginType();
  const callbackURL = getLoginCallbackUrl(redirect, { loginContext });
  const errorCallbackURL = getLoginCallbackUrl();

  const rejectLoginContext = useCallback(async () => {
    await session.logout();
    setRedirecting(false);
    setUserLoginState({ status: 'error', message: LOGIN_CONTEXT_ERROR_MESSAGE });
    message.error(LOGIN_CONTEXT_ERROR_MESSAGE);
  }, [message, session]);

  const isAllowedLoginUser = useCallback(
    (currentUser) => {
      if (!currentUser) {
        return false;
      }
      return adminLogin
        ? currentUser.isPlatformAdmin === true
        : currentUser.isPlatformAdmin !== true;
    },
    [adminLogin],
  );

  const redirectByOrganizationAccess = useCallback(async () => {
    if (hasHandledAuthenticatedRef.current) {
      return;
    }
    hasHandledAuthenticatedRef.current = true;
    setRedirecting(true);
    if (!isAllowedLoginUser(user)) {
      await rejectLoginContext();
      return;
    }
    if (adminLogin) {
      const nextPath = resolveLoginPostPath({
        loginContext,
        redirect,
        from: location.state?.from,
      });
      navigate(nextPath, { replace: true });
      return;
    }
    try {
      const { data, error: organizationError } = await authClient.organization.list();
      if (organizationError) {
        message.error(organizationError.message || '获取组织列表失败');
        navigate('/welcome', { replace: true });
        return;
      }
      const nextPath = resolveLoginPostPath({
        loginContext,
        redirect,
        from: location.state?.from,
        organizations: data,
      });
      navigate(nextPath, { replace: true });
    } catch {
      message.error('获取组织列表失败');
      navigate('/welcome', { replace: true });
    }
  }, [
    adminLogin,
    isAllowedLoginUser,
    location.state,
    loginContext,
    message,
    navigate,
    redirect,
    rejectLoginContext,
    user,
  ]);

  useEffect(() => {
    if (session.status !== 'authenticated') {
      hasHandledAuthenticatedRef.current = false;
      return;
    }
    redirectByOrganizationAccess();
  }, [redirectByOrganizationAccess, session.status]);

  useEffect(() => {
    if (!ssoLogin) {
      setFeishuLoginMode('checking');
      return undefined;
    }

    let active = true;

    const autoSignInWithFeishu = async () => {
      if (!isFeishuClient()) {
        setFeishuLoginMode('qr');
        return;
      }

      setFeishuLoginMode('auto-passwordless');
      try {
        await loadFeishuH5Sdk();
        await waitFeishuH5SdkReady();
        const code = await requestFeishuAccessCode();
        await signInWithFeishuJssdkCode(code);
        if (!active) {
          return;
        }
        setRedirecting(true);
        const refreshedUser = await session.refresh();
        if (!active) {
          return;
        }
        if (!refreshedUser) {
          setRedirecting(false);
          setUserLoginState({ status: 'error', message: '登录状态刷新失败，请重试' });
          message.error('登录状态刷新失败，请重试');
          return;
        }
        if (!isAllowedLoginUser(refreshedUser)) {
          await rejectLoginContext();
          return;
        }
        message.success('登录成功');
      } catch (error) {
        if (!active) {
          return;
        }
        const errorMessage = error?.response?.data?.message || error?.message || '飞书登录发生错误';
        setFeishuLoginMode('failed');
        setUserLoginState({ status: 'error', message: errorMessage });
        message.error(errorMessage);
      }
    };

    autoSignInWithFeishu();

    return () => {
      active = false;
    };
  }, [isAllowedLoginUser, message, rejectLoginContext, session, ssoLogin]);

  const handleSubmit = async (values) => {
    try {
      const { error } = await authClient.phoneNumber.verify({
        phoneNumber: values.phone,
        code: values.captcha,
      });
      if (error) {
        const errorMessage = resolveLoginErrorMessage(error);
        setUserLoginState({ status: 'error', message: errorMessage });
        message.error(errorMessage);
        return;
      }

      setRedirecting(true);
      const refreshedUser = await session.refresh();
      if (!refreshedUser) {
        setRedirecting(false);
        setUserLoginState({ status: 'error', message: '登录状态刷新失败，请重试' });
        message.error('登录状态刷新失败，请重试');
        return;
      }
      if (!isAllowedLoginUser(refreshedUser)) {
        await rejectLoginContext();
        return;
      }
      message.success('登录成功');
    } catch (error) {
      setUserLoginState({ status: 'error', message: '登录发生错误' });
      message.error('登录发生错误');
    }
  };

  const handleFeishuQrError = useCallback(
    (errorMessage) => {
      setUserLoginState({ status: 'error', message: errorMessage });
      message.error(errorMessage);
    },
    [message],
  );

  const handleGetCaptcha = async (phone) => {
    const { error } = await authClient.phoneNumber.sendOtp({
      phoneNumber: phone,
    });
    if (error) {
      message.error(resolveLoginErrorMessage(error, '发送验证码失败'));
      return;
    }
    message.success('验证码已发送，请注意查收');
  };

  const { message: loginMessage, status } = userLoginState;

  if (redirecting) {
    return <PageLoading />;
  }

  return (
    <LoginFormPage
      backgroundImageUrl={BACKGROUND_IMAGE_URL}
      backgroundVideoUrl={BACKGROUND_VIDEO_URL}
      logo="/favicon.png"
      submitter={ssoLogin ? HIDDEN_LOGIN_SUBMITTER : undefined}
      title={adminLogin ? '平台管理后台' : '餐饮连锁经营智能体'}
      subTitle={adminLogin ? '平台管理员登录' : '助远达助力您进入智能运营时代'}
      onFinish={async (values) => {
        if (!ssoLogin) {
          await handleSubmit(values);
        }
      }}
    >
      {status === 'error' && <LoginMessage content={loginMessage || '验证码错误'} />}

      {!ssoLogin && <PhoneOtpLoginForm onGetCaptcha={handleGetCaptcha} />}
      {ssoLogin && feishuLoginMode === 'checking' && <PageLoading />}
      {ssoLogin && feishuLoginMode === 'auto-passwordless' && (
        <>
          <Alert title="正在使用飞书免密登录" type="info" showIcon />
          <PageLoading />
        </>
      )}
      {ssoLogin && feishuLoginMode === 'qr' && (
        <FeishuQrLoginForm
          callbackURL={callbackURL}
          errorCallbackURL={errorCallbackURL}
          onError={handleFeishuQrError}
        />
      )}
    </LoginFormPage>
  );
};

Login.propTypes = {
  loginContext: PropTypes.oneOf(Object.values(LOGIN_CONTEXT)),
};

export default Login;
